import { useConversationConnection } from '../hooks/useConversationConnection';
import { startConversationPolling } from '../lib/conversation-polling';
import type { FormEvent, JSX } from 'react';
import { MarkdownMessage } from './MarkdownMessage';
import { TopicRuntimeStatus } from './TopicRuntimeStatus';
import { SessionEffortControl } from './SessionEffortControl';
import { TopicFollowUpStatus, topicMessageTime } from './TopicFollowUpStatus';
import { ConversationBoardPanel } from './ConversationBoardPanel';
import { ConversationSettingsPanel } from './ConversationSettingsPanel';
import { TopicAttachButton, TopicAttachmentList } from './TopicAttachments';
import { AssistantVoiceInput } from './AssistantVoiceInput';
import { nativeApp, onStashedShared, readNativeFile, takeStashedShared } from '../lib/native-app';
import type { TopicAttachmentDto } from '../api/types';
import '../styles/conversation-ux.css';
import { topicMessages } from '../lib/topic-messages';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  type AgentPathDto,
  type ConversationTopicDetailDto,
  type ConversationTopicDto,
} from '../api/client';

const LEGACY_GENERAL_ID = 'topic_general_legacy';

function topicStatus(topic: ConversationTopicDto): string {
  if (topic.activeTurnTaskRunId || topic.status === 'working') return 'respondendo';
  if (topic.queue.some((entry) => entry.status === 'deferred')) return 'aguardando recursos';
  if (topic.queue.some((entry) => entry.status === 'queued')) return 'na fila';
  if (topic.queue.some((entry) => entry.status === 'failed')) return 'falhou';
  if (topic.context.state === 'suspended') return 'em espera';
  return topic.status === 'waiting' ? 'atenção' : 'pronto';
}

function acpPathState(path: AgentPathDto): string {
  if (path.enabled === false) return 'desabilitada';
  if (path.capability?.availability === 'available') return 'disponível';
  if (path.capability?.availability === 'unavailable') return 'indisponível agora';
  return 'capacidade ainda não observada';
}

function queueStatus(topic: ConversationTopicDto): string | null {
  const entry = topic.queue.at(-1);
  if (!entry) return null;
  const route = entry.agentPathId ? ` · ${entry.agentPathId}` : '';
  switch (entry.status) {
    case 'queued':
      return `Recebido e enfileirado${route}. O assunto continua disponível enquanto aguarda capacidade.`;
    case 'working':
      return `Em execução${route} · tentativa ${entry.attempt}.`;
    case 'deferred': {
      const next = entry.nextAttemptAt
        ? new Date(entry.nextAttemptAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : 'quando houver capacidade';
      const reason = entry.lastErrorCode ? ` · ${entry.lastErrorCode}` : '';
      return `Retry durável agendado para ${next}${route} · adiamentos ${entry.deferCount}${reason}.`;
    }
    case 'failed':
      return `Falha após ${entry.attempt} tentativa(s)${route}${entry.lastErrorCode ? ` · ${entry.lastErrorCode}` : ''}. A mensagem ficou registrada no assunto.`;
    case 'completed':
      return `Concluído${route}.`;
  }
}

function projectLabel(subjectCwd: string | null): string | null {
  if (!subjectCwd) return null;
  const segments = subjectCwd.replace(/[\\/]+$/, '').split(/[\\/]/);
  return segments[segments.length - 1] || null;
}

const projectChipStyle = {
  overflow: 'hidden',
  color: 'var(--accent, #ffcc66)',
  font: '10px/1.3 ui-monospace, monospace',
  fontStyle: 'normal',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
const subjectStyle = {
  margin: '3px 0 0',
  color: 'var(--muted, #707a8c)',
  font: '10px/1.4 ui-monospace, monospace',
} as const;

/**
 * CO — a Conversa é o orquestrador pessoal do dono: uma conversa contínua (o assunto padrão) com um quadro
 * de apontamentos ao lado. Assuntos adicionais continuam possíveis, mas saem do caminho principal.
 * Mensagens de controle do sistema nunca são cartões de conversa: viram atividade do quadro.
 */
/**
 * CO — avisos de produto gravados como `control` ANTES do quadro existir (legado, preservado no dado). Nunca
 * viram cartão: entram recolhidos, um grupo por sequência contígua, para o histórico não ficar poluído.
 */
const LEGACY_NOTICE = /^(Produto · pedido|Resultado do Produto|O produto concluiu|proposta ao MASTER)/u;
type TranscriptItem = ReturnType<typeof topicMessages>[number];
function groupLegacyNotices(entries: TranscriptItem[]): Array<TranscriptItem | { seq: number; legacy: TranscriptItem[] }> {
  const out: Array<TranscriptItem | { seq: number; legacy: TranscriptItem[] }> = [];
  for (const entry of entries) {
    if (entry.role === 'control' && LEGACY_NOTICE.test(entry.text)) {
      const last = out.at(-1);
      if (last && 'legacy' in last) last.legacy.push(entry);
      else out.push({ seq: entry.seq, legacy: [entry] });
    } else out.push(entry);
  }
  return out;
}

export function ConversationTopicsPanel({
  activeTopicId,
  onActiveTopic,
}: {
  activeTopicId: string;
  onActiveTopic: (topicId: string) => void;
}): JSX.Element {
  const [showArchived, setShowArchived] = useState(false);
  const [topics, setTopics] = useState<ConversationTopicDto[]>([]);
  const [detail, setDetail] = useState<ConversationTopicDetailDto | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<'conversa' | 'quadro'>('conversa');
  const [boardFocus, setBoardFocus] = useState<string | null>(null);
  // DD.4 (24/09/2026) — a chamada ao dono (Telegram/push) traz `?apontamentos=1&pedido=<id>`: abre direto no quadro, no
  // pedido. Aplicado uma única vez, no primeiro assunto ativo (o efeito de troca de assunto voltava a vista para a conversa).
  const urlBoardRequest = useRef<{ pedido: string | null } | null>((() => {
    try {
      const params = new URL(window.location.href).searchParams;
      return params.get('apontamentos') ? { pedido: params.get('pedido') } : null;
    } catch { return null; }
  })());
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    try { const stored: unknown = JSON.parse(sessionStorage.getItem('td_personal_drafts') ?? '{}'); return stored && typeof stored === 'object' && !Array.isArray(stored) ? Object.fromEntries(Object.entries(stored).filter(([, value]) => typeof value === 'string')) as Record<string, string> : {}; } catch { return {}; }
  });
  const draft = drafts[activeTopicId] ?? '';
  const setDraft = (value: string): void => setDrafts(current => ({ ...current, [activeTopicId]: value }));
  useEffect(() => { try { sessionStorage.setItem('td_personal_drafts', JSON.stringify(drafts)); } catch { /* quota unavailable; in-memory draft remains */ } }, [drafts]);
  const submissionKeys = useRef(new Map<string, { id: string; messageSeq: number }>());
  const restoredSubmissions = useRef(false);
  if (!restoredSubmissions.current) {
    restoredSubmissions.current = true;
    try { for (const [key, value] of JSON.parse(sessionStorage.getItem('td_pending_submissions') ?? '[]')) if (typeof key === 'string' && typeof value?.id === 'string' && Number.isInteger(value.messageSeq)) submissionKeys.current.set(key, value); } catch { /* preserve in-memory state */ }
  }
  const persistSubmissions = () => { try { sessionStorage.setItem('td_pending_submissions', JSON.stringify([...submissionKeys.current])); } catch { /* same-tab retry remains idempotent */ } };
  const [attachmentsByTopic, setAttachmentsByTopic] = useState<Record<string, TopicAttachmentDto[]>>(() => {
    try { return JSON.parse(sessionStorage.getItem('td_topic_attachment_drafts') ?? '{}'); } catch { return {}; }
  });
  useEffect(() => { try { sessionStorage.setItem('td_topic_attachment_drafts', JSON.stringify(attachmentsByTopic)); } catch { /* retain in memory */ } }, [attachmentsByTopic]);
  const attachments = attachmentsByTopic[activeTopicId] ?? [];
  const [uploading, setUploading] = useState(false);
  const uploadLock = useRef(false);
  const addFiles = async (files: File[]): Promise<void> => {
    if (uploadLock.current || !activeTopicId) return;
    const target = activeTopicId;
    if (files.length + attachments.length > 4 || files.some(file => file.size > 10 * 1024 * 1024 || !file.size)) { setError('Escolha até 4 anexos, com no máximo 10 MB por arquivo e conteúdo não vazio.'); return; }
    uploadLock.current = true; setUploading(true); setError(null);
    try {
      for (const file of files) {
        const data = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(',')[1]!); reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.')); reader.readAsDataURL(file); });
        const attachment = await api.uploadConversationAttachment(target, { name: file.name || 'imagem-colada.png', mimeType: file.type, data });
        setAttachmentsByTopic(current => ({ ...current, [target]: [...(current[target] ?? []).filter(a => a.id !== attachment.id), attachment] }));
      }
    } catch (cause) { setError(String(cause)); } finally { uploadLock.current = false; setUploading(false); }
  };
  // ECO.18 — "Compartilhar → term-deck" no app Android: texto vai ao rascunho, imagem vira anexo e áudio (ex.: nota
  // de voz do WhatsApp) é transcrito para o rascunho, tudo no assunto aberto; o dono revisa e envia.
  const [sharedNotice, setSharedNotice] = useState<string | null>(null);
  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;
  useEffect(() => {
    if (!activeTopicId) return undefined;
    const target = activeTopicId;
    const append = (text: string): void => setDrafts(current => {
      const previous = current[target] ?? '';
      return { ...current, [target]: previous.trim() ? `${previous}\n${text}` : text };
    });
    const consume = async (): Promise<void> => {
      const content = takeStashedShared();
      if (!content) return;
      const plugin = nativeApp();
      const done: string[] = [];
      const pieces = [content.subject, content.text].filter((v): v is string => Boolean(v?.trim()));
      if (content.subject && content.text?.includes(content.subject)) pieces.shift();
      if (pieces.length) { append(pieces.join('\n')); done.push('texto no campo'); }
      const images: File[] = [];
      for (const file of content.files ?? []) {
        if (!plugin) break;
        try {
          const blob = await readNativeFile(plugin, 'shared', file.id, file.mimeType);
          if (file.mimeType.startsWith('audio/')) {
            setSharedNotice('transcrevendo o áudio recebido…');
            const text = await api.transcribeForTerminal(blob, file.name || 'compartilhado.ogg');
            if (text.trim()) { append(text.trim()); done.push('áudio transcrito'); }
          } else if (file.mimeType.startsWith('image/')) {
            images.push(new File([blob], file.name || 'imagem.jpg', { type: file.mimeType }));
          } else {
            done.push(`${file.name}: tipo não suportado`);
          }
        } catch (cause) {
          done.push(`${file.name}: ${cause instanceof Error ? cause.message : String(cause)}`);
        } finally {
          void plugin.discardShared({ id: file.id }).catch(() => undefined);
        }
      }
      if (content.failedFiles) done.push(content.failedFiles === 1 ? '1 arquivo não pôde ser lido (tente compartilhar de novo ou anexar pelo clipe)' : `${content.failedFiles} arquivos não puderam ser lidos (tente compartilhar de novo ou anexar pelo clipe)`);
      if (images.length) { await addFilesRef.current(images); done.push(images.length === 1 ? 'imagem anexada' : `${images.length} imagens anexadas`); }
      setSharedNotice(done.length ? `Recebido do compartilhamento: ${done.join(' · ')}` : null);
    };
    void consume();
    return onStashedShared(() => void consume());
  }, [activeTopicId]);
  const currentTopicId = useRef(activeTopicId);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const followTailRef = useRef(true);
  const [followingTail, setFollowingTail] = useState(true);
  useEffect(() => {
    followTailRef.current = true; setFollowingTail(true);
    const request = activeTopicId ? urlBoardRequest.current : null;
    if (request) {
      urlBoardRequest.current = null;
      setBoardFocus(request.pedido); setView('quadro');
      try {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('apontamentos'); clean.searchParams.delete('pedido');
        window.history.replaceState(null, '', `${clean.pathname}${clean.search}${clean.hash}`);
      } catch { /* sem history: a vista já foi aplicada */ }
      return;
    }
    setBoardFocus(null); setView('conversa');
  }, [activeTopicId]);
  // AK.6 — o quadro abre pela ÂNCORA (faixa de objetivos), nunca pela posição em que a conversa estava rolada: o container
  // de rolagem é o mesmo e a conversa vive no fim; sem isto o dono aterrissava no último apontamento (medido na 0.500.0).
  // Medido na 0.500.1: a `transcriptRef` pertence ao div da CONVERSA e fica nula na troca, enquanto o React reaproveita o
  // MESMO nó DOM para o quadro (mesmo tipo, mesma posição) — e o nó guarda o scrollTop (5329 medido). Ref própria do quadro.
  const boardHostRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (view === 'quadro' && !boardFocus && boardHostRef.current) boardHostRef.current.scrollTop = 0; }, [view, boardFocus]);
  useEffect(() => {
    const node = transcriptRef.current;
    if (node && followTailRef.current) node.scrollTop = node.scrollHeight;
  }, [detail?.transcript, activeTopicId, view]);
  currentTopicId.current = activeTopicId;
  const [sending, setSending] = useState(false);
  const [agentPaths, setAgentPaths] = useState<AgentPathDto[]>([]);
  const [selectedPathId, setSelectedPathId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { readError, failure: readFailure, success: readSuccess, events: connectionEvents } = useConversationConnection();
  const resolvingHumanTopic = useRef(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    void api.agentPaths()
      .then((paths) => {
        if (!alive) return;
        const acpPaths = paths.filter((path) => path.transport === 'acp' && path.enabled !== false);
        setAgentPaths(acpPaths); readSuccess('modelos');
      })
      .catch((cause) => {
        if (alive) readFailure('modelos', cause);
      });
    return () => { alive = false; };
  }, [readFailure, readSuccess]);

  const lastTopics = useRef('');
  const refresh = useCallback(async (signal?: AbortSignal): Promise<boolean> => {
    try {
      const next = await api.conversationTopics(showArchived, signal);
      if (signal?.aborted) return false;
      const signature = JSON.stringify(next);
      const changed = signature !== lastTopics.current;
      lastTopics.current = signature;
      setTopics(next);
      setLoaded(true);
      readSuccess('assuntos');
      const humanTopics = next.filter(
        (topic) => topic.id !== LEGACY_GENERAL_ID && (showArchived || topic.status !== 'archived'),
      );
      const activeIsHuman = humanTopics.some((topic) => topic.id === activeTopicId);
      if (!activeIsHuman) {
        const selected = humanTopics.find((topic) => topic.selected) ?? humanTopics[0];
        if (selected) {
          onActiveTopic(selected.id);
        } else if (!resolvingHumanTopic.current) {
          resolvingHumanTopic.current = true;
          try {
            const resolved = await api.resolveHumanConversationTopic();
            if (signal?.aborted) return false;
            setTopics((current) => [
              ...current.filter((topic) => topic.id !== resolved.id),
              resolved,
            ]);
            onActiveTopic(resolved.id);
          } finally {
            resolvingHumanTopic.current = false;
          }
        }
      }
      return changed;
    } catch (cause) {
      if (!signal?.aborted) readFailure('assuntos', cause);
      return false;
    }
  }, [activeTopicId, onActiveTopic, showArchived, readFailure, readSuccess]);

  useEffect(() => {
    return startConversationPolling(refresh, 3_000);
  }, [refresh]);

  useEffect(() => {
    if (!activeTopicId || activeTopicId === LEGACY_GENERAL_ID) { setDetail(null); return; }
    setDetail(null);
    let signature = '';
    const load = async (signal: AbortSignal): Promise<boolean> => {
      try {
        const next = await api.conversationTopic(activeTopicId, signal);
        if (signal.aborted) return false;
        // generatedAt muda em toda consulta; só conteúdo/atividade reinicia o piso.
        const stamp = JSON.stringify([next.topic, next.transcript, next.proposals, next.board?.items]);
        const changed = stamp !== signature;
        signature = stamp;
        setDetail(next); readSuccess('conversa');
        return changed || Boolean(next.topic.activeTurnTaskRunId);
      } catch (cause) {
        if (!signal.aborted) readFailure('conversa', cause);
        return false;
      }
    };
    return startConversationPolling(load, 2_000);
  }, [activeTopicId, readFailure, readSuccess]);

  const visibleTopics = useMemo(() => {
    return topics.filter(
      (topic) => topic.id !== LEGACY_GENERAL_ID && (showArchived || topic.status !== 'archived'),
    );
  }, [topics, showArchived]);

  const select = async (topicId: string): Promise<void> => {
    onActiveTopic(topicId);
    try { await api.selectConversationTopic(topicId); await refresh(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
  };

  const create = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      const topic = await api.createConversationTopic({ title, domain: 'geral' });
      setNewTitle('');
      onActiveTopic(topic.id);
      await api.selectConversationTopic(topic.id);
      await refresh();
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setCreating(false); }
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const text = draft.trim();
    if ((!text && !attachments.length) || !detail || detail.topic.id !== activeTopicId || sending || uploading) return;
    const targetId = detail.topic.id;
    const attachmentIds = attachments.map(a => a.id);
    const submissionKey = JSON.stringify([targetId, text, attachmentIds]);
    setSending(true);
    try {
      let fresh = await api.conversationTopic(targetId);
      const envelope = submissionKeys.current.get(submissionKey) ?? { id: crypto.randomUUID(), messageSeq: fresh.topic.nextMessageSeq };
      submissionKeys.current.set(submissionKey, envelope);
      persistSubmissions();
      const submitEnvelope = () => api.submitConversationTopic(targetId, {
        topicRevision: fresh.topic.revision,
        clientSubmissionId: envelope.id,
        messageSeq: envelope.messageSeq,
        attachmentIds,
        text,
        ...(selectedPathId ? { pathId: selectedPathId } : {}),
      });
      let next: ConversationTopicDetailDto;
      try { next = await submitEnvelope(); }
      catch (cause) {
        // A named CAS refusal proves no new enqueue. Network ambiguity keeps the identical envelope.
        if (!/topic_(revision|message_seq)_conflict/.test(String(cause))) throw cause;
        fresh = await api.conversationTopic(targetId);
        if (!fresh.topic.queue.some(entry => entry.clientSubmissionId === envelope.id)) envelope.messageSeq = fresh.topic.nextMessageSeq;
        persistSubmissions();
        next = await submitEnvelope();
      }
      if (currentTopicId.current === targetId) setDetail(next);
      setDrafts(current => current[targetId]?.trim() === text ? { ...current, [targetId]: '' } : current);
      setAttachmentsByTopic(current => ({ ...current, [targetId]: (current[targetId] ?? []).filter(a => !attachmentIds.includes(a.id)) }));
      submissionKeys.current.delete(submissionKey);
      persistSubmissions();
      setView('conversa');
      await refresh();
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setSending(false); }
  };

  const archive = async (): Promise<void> => {
    if (!detail || detail.topic.activeTurnTaskRunId || detail.topic.queue.some(s => ['queued', 'working', 'deferred'].includes(s.status))) return;
    if (!window.confirm('Arquivar este assunto? O histórico e os apontamentos ficam preservados para consulta. Para apenas começar um contexto novo, cancele e use Reiniciar contexto.')) return;
    try {
      await api.archiveConversationTopic(detail.topic.id);
      onActiveTopic('');
      await refresh();
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
  };

  const busyTopic = Boolean(detail?.topic.activeTurnTaskRunId) || Boolean(detail?.topic.queue.some(s => ['queued', 'working', 'deferred'].includes(s.status)));
  const openCount = detail?.board?.open ?? 0;
  const onRetry = (submissionId: string): void => {
    if (!detail) return;
    setSending(true); setError(null);
    void api.retryConversationTopic(detail.topic.id, detail.topic.revision, submissionId).then(next => { if (currentTopicId.current === next?.topic.id) { setDetail(next); setView('conversa'); } }).catch(cause => setError(String(cause))).finally(() => setSending(false));
  };
  const onReconcile = (): void => {
    if (!detail) return;
    setSending(true); setError(null);
    void api.reconcileConversationProductRouting(detail.topic.id).then(result => { if (currentTopicId.current === result.topic?.topic.id) setDetail(result.topic); }).catch(cause => setError(String(cause))).finally(() => setSending(false));
  };

  return (
    <section className="topic-workspace" data-testid="conversation-topics">
      {loaded && visibleTopics.length > 1 ? (
        <div className="topic-rail" role="tablist" aria-label="assuntos da conversa">
          {visibleTopics.map((topic) => {
            const project = projectLabel(topic.subjectCwd);
            return (
              <button
                type="button"
                role="tab"
                aria-selected={activeTopicId === topic.id}
                className={activeTopicId === topic.id ? 'topic-tab is-active' : 'topic-tab'}
                key={topic.id}
                title={activeTopicId === topic.id ? 'assunto em foco' : `trocar para “${topic.title}”`}
                onClick={() => void select(topic.id)}
              >
                <span>{topic.title}{topic.status === 'archived' ? ' · Arquivado' : ''}</span>
                {project ? <em style={projectChipStyle}>{project}</em> : null}
                <small>{topicStatus(topic)}</small>
                {topic.unreadCount > 0 && activeTopicId !== topic.id ? <b>{topic.unreadCount}</b> : null}
              </button>
            );
          })}
        </div>
      ) : null}
      {(error || readError) ? <p className="topic-error" role="alert">{error || readError}</p> : null}
      {connectionEvents.length ? <details className="topic-connection-history"><summary>Histórico de conexão · {readError ? 'atualização pendente' : 'últimas ocorrências'}</summary>
        <p>Últimas ocorrências desta aba. Uma leitura bem-sucedida só encerra a falha do mesmo recurso.</p>
        <ul>{[...connectionEvents].reverse().map((e, i) => <li key={`${e.at}-${i}`}><time>{new Date(e.at).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</time> · {e.resource}: {e.recovered ? 'recuperado' : e.status ? `falha HTTP ${e.status}` : 'falha de conexão'}{e.trace ? <small> · recibo {e.trace}</small> : null}</li>)}</ul>
      </details> : null}
      {activeTopicId && activeTopicId !== LEGACY_GENERAL_ID ? (
        <div className="topic-conversation" data-testid="topic-conversation">
          {detail && detail.topic.id === activeTopicId ? (
            <>
              <header>
                <div>
                  <small>CONVERSA CONTÍNUA</small>
                  <h2>{detail.topic.title === 'Conversa' && detail.topic.domain === 'geral' && !detail.topic.subjectCwd ? 'Assistente' : detail.topic.title}</h2>
                  {projectLabel(detail.topic.subjectCwd) ? (
                    <p data-testid="topic-subject" style={subjectStyle}>
                      projeto: {projectLabel(detail.topic.subjectCwd)}
                    </p>
                  ) : null}
                </div>
                <nav className="topic-view-switch" aria-label="modo de visualização">
                  <button type="button" aria-pressed={view === 'conversa'} onClick={() => setView('conversa')}>Conversa</button>
                  <button type="button" aria-pressed={view === 'quadro'} data-testid="topic-view-board" onClick={() => { setBoardFocus(null); setView('quadro'); }}>
                    Apontamentos{openCount ? <b title="Tarefas em aberto no assunto inteiro, antes dos filtros">{openCount} abertos</b> : null}
                  </button>
                </nav>
                <ConversationSettingsPanel key={detail.topic.id} topicId={detail.topic.id} />
                <details className="topic-context-menu"><summary>Mais</summary><div>
                  <p>Reiniciar começa uma sessão nova neste assunto. Histórico e apontamentos permanecem; nenhuma ação é reenviada.</p>
                  <button type="button" disabled={sending || detail.topic.status === 'archived' || busyTopic} onClick={() => {
                    if (!window.confirm('Reiniciar somente o contexto do assistente? Seu histórico, apontamentos e ações confirmadas serão preservados. Nenhum pedido será executado agora.')) return;
                    setSending(true); setError(null);
                    void api.resetConversationContext(detail.topic.id, detail.topic.revision).then(next => { if (currentTopicId.current === next?.topic.id) setDetail(next); }).catch(cause => setError(String(cause))).finally(() => setSending(false));
                  }}>Reiniciar contexto</button>
                  <p>Você pode usar o Assistente para tudo nesta conversa. Criar assuntos separados é opcional e mantém históricos próprios.</p>
                  <form className="topic-create" onSubmit={(event) => void create(event)} aria-label="criar um assunto novo">
                    <input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="nome do assunto" aria-label="nome do novo assunto" maxLength={120} />
                    <button type="submit" disabled={creating || !newTitle.trim()}>{creating ? 'criando…' : 'criar assunto'}</button>
                  </form>
                  <button type="button" className="topic-archive-toggle" onClick={() => setShowArchived(value => !value)}>{showArchived ? 'Ocultar arquivados' : 'Ver arquivados'}</button>
                  <p>Arquivar encerra este assunto, mantendo histórico e pendências para consulta.</p>
                  <button type="button" onClick={() => void archive()} disabled={sending || busyTopic}>Arquivar assunto</button>
                </div></details>
              </header>
              {/* DD.17 — estado e esforço da sessão da Conversa são da Conversa; em Apontamentos só tiravam espaço. */}
              {view === 'conversa' ? <div className="topic-execution-panel">
                <TopicRuntimeStatus topic={detail.topic} />
                <SessionEffortControl topic={detail.topic} onChange={next => { if (currentTopicId.current === next?.topic.id) setDetail(next); }} />
              </div> : null}
              {view === 'conversa' ? <TopicFollowUpStatus detail={detail} onOpen={id => { setBoardFocus(id); setView('quadro'); }} /> : null}
              {view === 'quadro' ? (
                <div className="topic-transcript topic-board-host" ref={boardHostRef}>
                  <ConversationBoardPanel key={boardFocus ?? 'all'} initialSubmissionId={boardFocus} detail={detail} sending={sending} onRetry={onRetry} onReconcile={onReconcile} onManage={async (pointId, input) => {
                    try { const result = await api.manageConversationPoint(detail.topic.id, pointId, input); if (currentTopicId.current === result.topic?.topic.id) setDetail(result.topic); }
                    catch (cause) { const latest = await api.conversationTopic(detail.topic.id).catch(() => null); if (latest && currentTopicId.current === detail.topic.id) setDetail(latest); throw cause; }
                  }} onObjective={async input => {
                    // AK.6 — ações do dono sobre objetivos/tarefas (um clique); o quadro é relido depois de qualquer resultado.
                    try { await api.manageConversationObjective(input); }
                    finally { const latest = await api.conversationTopic(detail.topic.id).catch(() => null); if (latest && currentTopicId.current === detail.topic.id) setDetail(latest); }
                  }} />
                </div>
              ) : (
                <div className="topic-transcript" aria-live="polite" ref={transcriptRef} onScroll={event => {
                  const node = event.currentTarget;
                  const follow = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
                  followTailRef.current = follow; setFollowingTail(follow);
                }}>
                  {detail.transcript.length === 0 ? <p className="topic-empty">Faça seus apontamentos aqui. O assistente separa, delega aos terminais e acompanha até o fim; o quadro mostra o andamento.</p> : groupLegacyNotices(topicMessages(detail.transcript, detail.topic.queue)).map((entry) => (
                    'legacy' in entry
                      ? <details className="topic-system-legacy" key={`legacy-${entry.seq}`}><summary>{entry.legacy.length} {entry.legacy.length === 1 ? 'aviso antigo do sistema' : 'avisos antigos do sistema'} · hoje isso fica no quadro</summary>
                        {entry.legacy.map(notice => <p className="topic-system-note" key={notice.seq}>{notice.text}</p>)}</details>
                      : entry.role === 'control'
                        ? <p className="topic-system-note" key={entry.seq} data-testid="topic-system-note">{entry.text}</p>
                        : <article className={`topic-message topic-message--${entry.role}`} key={entry.seq}>
                          <small><span>{entry.role === 'user' ? 'você' : 'assistente'}</span> · <time dateTime={entry.at}>{topicMessageTime(entry.at)}</time></small>
                          <MarkdownMessage text={entry.text} query="" />
                          {entry.attachments?.length ? <TopicAttachmentList topicId={detail.topic.id} attachments={entry.attachments} /> : null}
                        </article>
                  ))}
                </div>
              )}
              {view === 'conversa' && !followingTail ? <button className="topic-tail" type="button" onClick={() => {
                followTailRef.current = true; setFollowingTail(true);
                if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
              }}>Ver resposta mais recente ↓</button> : null}
              {queueStatus(detail.topic) && detail.topic.queue.at(-1)?.status !== 'completed' ? (
                <details className="topic-submit-details" open={detail.topic.queue.at(-1)?.status === 'failed'}>
                  <summary>{topicStatus(detail.topic)}</summary>
                  <p data-testid="topic-submit-status" role={detail.topic.queue.at(-1)?.status === 'failed' ? 'alert' : 'status'}>{queueStatus(detail.topic)}</p>
                </details>
              ) : null}
              {detail.topic.status === 'archived' ? <p className="topic-archived-notice">Assunto arquivado · histórico e apontamentos preservados para consulta. Crie um novo assunto para conversar.</p> : <form className="topic-composer" onSubmit={(event) => void submit(event)} onPaste={event => { const files = Array.from(event.clipboardData.files); if (files.length) { event.preventDefault(); void addFiles(files); } }}>
                {attachments.length ? <TopicAttachmentList topicId={activeTopicId} attachments={attachments} onRemove={id => setAttachmentsByTopic(current => ({ ...current, [activeTopicId]: (current[activeTopicId] ?? []).filter(a => a.id !== id) }))} /> : null}
                {uploading ? <small role="status">Preparando anexo…</small> : null}
                {agentPaths.some(path => !path.id.startsWith('builtin.')) ? (
                  <label style={{ ...subjectStyle, display: 'grid', gridColumn: '1 / -1', gap: 6 }}>
                    Modelo
                    <select
                      aria-label="Modelo do Assistente"
                      value={selectedPathId}
                      onChange={(event) => setSelectedPathId(event.target.value)}
                    >
                      <option value="">Preferência do Assistente</option>
                      {agentPaths.filter(path => !['builtin.codex-conversation', 'builtin.claude-conversation'].includes(path.id)).map((path) => (
                        <option key={path.id} value={path.id}>
                          {path.providerBinding} · {acpPathState(path)}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {agentPaths.length === 0 ? (
                  <small role="status" style={{ gridColumn: '1 / -1' }}>Assistente da conversa indisponível. O texto fica preservado; nenhum envio ao PTY operacional será tentado.</small>
                ) : null}
                {sharedNotice ? (
                  <div className="topic-shared-notice" role="status" data-testid="topic-shared-notice">
                    <span>{sharedNotice}</span>
                    <button type="button" onClick={() => setSharedNotice(null)} aria-label="fechar aviso">ok</button>
                  </div>
                ) : null}
                <TopicAttachButton onFiles={files => void addFiles(files)} disabled={uploading || sending} />
                <textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Faça um apontamento, peça algo ou pergunte como está…" aria-label="mensagem do assunto" rows={3} />
                {/* ECO.6 — voz: o texto transcrito entra no rascunho DESTE assunto; o dono revisa e envia. */}
                <AssistantVoiceInput
                  disabled={sending}
                  onTranscript={text => setDrafts(current => { const previous = current[activeTopicId] ?? ''; return { ...current, [activeTopicId]: previous.trim() ? `${previous} ${text}` : text }; })}
                />
                <button type="submit" disabled={sending || uploading || (!draft.trim() && !attachments.length) || agentPaths.length === 0}>{sending ? 'enfileirando…' : 'enviar'}</button>
              </form>}
            </>
          ) : <div className="topic-empty">carregando assunto…</div>}
        </div>
      ) : null}
    </section>
  );
}
